import type { TagValue, UserInstrumentClassificationDto, UserInstrumentDto } from "@eu.mdotm.api/sphere";
import { mainMenuItemsForUserClassification } from "$root/functional-areas/instruments/Instrument-customisation/menu-items";
import {
	AgGridTooltipWrapper,
	richTextClipboardItem,
	richTextMarker,
	useAgGridRowListener,
} from "$root/third-party-integrations/ag-grid/ag-grid";
import {
	builtInCaseInsensitiveSort,
	builtInCaseInsensitiveSortFor,
	coalesce,
	qualifier,
	sumArrayLike,
} from "$root/utils";
import { Column, Row, Text } from "@mdotm/shadcn/legacy/components";
import { type AutocompleteHandle, Autocomplete } from "@mdotm/shadcn/mdotm/components";
import { useEventListener, useRefProxyWithState } from "@mdotm/shadcn/legacy/react-extensions";
import { approxRound, builtInSort, stableEmptyArray } from "@mdotm/shadcn/legacy/utils";
import type { ColDef } from "ag-grid-community";
import type { CustomCellEditorProps, CustomCellRendererProps, CustomTooltipProps } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { tagColorMap } from "../../colors";
import type { ColumnOverrideParams } from "../../column/common-columns-definition";
import { TagBadge } from "./Tag";
import { TagCell, TagWeightList } from "./tag-views";

export function tagWeightColDef<T extends UserInstrumentDto = UserInstrumentDto>(
	classification: UserInstrumentClassificationDto,
	opt?: Omit<
		ColumnOverrideParams<T, Array<TagValue>>,
		"tooltipComponent" | "cellRenderer" | "cellEditor" | "tooltipValueGetter" | "valueGetter" | "valueSetter"
	>,
): ColDef<T, Array<TagValue>> {
	return {
		colId: classification.classificationUuid!,
		headerName: classification.name ?? "-",
		suppressKeyboardEvent: (params) => params.editing,
		tooltipComponent: agTagTooltip(classification),
		cellRenderer: agTagCell(classification),
		cellEditor: agTagEditor(classification),
		mainMenuItems: mainMenuItemsForUserClassification(classification),
		valueFormatter: ({ value }) => {
			return value == null
				? ""
				: richTextClipboardItem({
						html: {
							style: `body,span { font:sans-serif }`,
							bodyContent: value.map((tag, i) => (
								<React.Fragment key={`${tag.option}-${i}`}>
									<span
										style={{
											backgroundColor:
												tagColorMap[
													classification.options?.find((o) => o.option === tag.option)?.color ?? "GRAY"
												].badgeColor(),
										}}
									>
										<span>{tag.option}</span> <span>({tag.weight}%)</span>
									</span>
									{i < value.length - 1 ? " " : ""}
								</React.Fragment>
							)),
						},
						plain: tagsToClipboardStr(value),
					});
		},
		valueParser: (params) => {
			const { newValue, oldValue } = params;
			if (newValue.trim() === "") {
				return [];
			}
			let rawTags: TagValue[] = [];
			if (newValue.includes(richTextMarker)) {
				const jsonResult = tagWeightClipboardToJson(newValue);
				if (jsonResult) {
					rawTags = tagWeightStringParser(jsonResult);
				}
			} else {
				rawTags = tagWeightStringParser(newValue);
			}

			const filtered = rawTags
				.filter((tag) => !Number.isNaN(tag.weight))
				.filter((tag) => classification.options?.some((valid) => valid.option === tag.option));
			if (filtered.length < rawTags.length) {
				return oldValue; // invalid tag detected, preventing paste as it might be cause unwanted changes
			}
			const normalizedTags = normalizeTags(filtered);

			return normalizedTags;
		},
		cellEditorPopup: true,
		cellEditorPopupPosition: "under",
		tooltipValueGetter: (row) =>
			row.value == null
				? ""
				: (row.data?.tags?.find((t) => t.classificationUuid === classification.classificationUuid)?.values ?? []),
		valueGetter: (row) =>
			row.data?.tags?.find((t) => t.classificationUuid === classification.classificationUuid)?.values ?? [],
		keyCreator: (row) =>
			[
				...(row.data?.tags
					?.find((t) => t.classificationUuid === classification.classificationUuid)
					?.values?.map((v) => v.option) ?? stableEmptyArray),
			]
				.sort(builtInSort)
				.join(",") ?? "",
		valueSetter: (row) => {
			const field = "tags" as const;
			const candidate = row.data[field]?.find((s) => s.classificationUuid === classification.classificationUuid);
			if (candidate) {
				candidate.values = coalesce(row.newValue, []);
			} else {
				row.data[field] = (row.data[field] ?? []).concat({
					classificationUuid: classification.classificationUuid,
					standardClassificationId: classification.standardClassificationId,
					values: coalesce(row.newValue, []),
				});
			}
			return true;
		},
		comparator(a, b) {
			return builtInCaseInsensitiveSort(
				a?.slice().sort(builtInCaseInsensitiveSortFor("option"))?.[0]?.option,
				b?.slice().sort(builtInCaseInsensitiveSortFor("option"))?.[0]?.option,
			);
		},
		...(opt ?? {}),
	};
}

/** matches strings formatted as "Tag1 (11%) Tag2 (20%)"
 * or "Tag1 (11%); Tag2 (20%)", with various combination
 * of number formats, such as 1.20, 1., 1, .20
 **/
function tagWeightStringParser(newValue: string) {
	const regex = /\s*(.+?)\s+\(((\.\d+)|(\d+(\.\d*)?))%\);?\s*/g;
	const rawTags: TagValue[] = [];
	let match;
	while ((match = regex.exec(newValue))) {
		rawTags.push({
			option: match[1],
			weight: Number(match[2]),
		});
	}
	return rawTags;
}

function tagWeightClipboardToJson(newValue: string): string | undefined {
	try {
		const parsedJson = JSON.parse(newValue.replace(richTextMarker, ""));
		return parsedJson["text/plain"];
	} catch (error) {
		console.error(error);
		return undefined;
	}
}

function tagsToClipboardStr(value: TagValue[]): string {
	return value.map((tag) => `${tag.option} (${tag.weight}%)`).join("; ");
}

function agTagCell(classification: UserInstrumentClassificationDto) {
	return function cellRenderer(
		row: CustomCellRendererProps<UserInstrumentDto, Array<TagValue>>,
	): React.JSX.Element | string {
		if (row.value == null || row.value.length === 0) {
			return "";
		}

		return <TagCell classification={classification} values={row.value ?? []} />;
	};
}

function agTagTooltip(classification: UserInstrumentClassificationDto) {
	return function TooltipComponent(
		row: CustomTooltipProps<UserInstrumentDto, Array<TagValue>>,
	): React.JSX.Element | string {
		useAgGridRowListener(row, "cellEditingStarted", () => row.hideTooltipCallback?.());

		if (row.value == null || row.value.length === 0) {
			return "";
		}

		return (
			<AgGridTooltipWrapper className="min-w-[224px]">
				<TagWeightList
					showUntagged
					options={classification.options}
					values={row.value ?? []}
					visualization={
						classification.dataVisualization === "TAG_VIEW_WITHOUT_COLOR"
							? "TAG_VIEW_WITHOUT_COLOR"
							: "TAG_VIEW_WITH_COLOR"
					}
				/>
			</AgGridTooltipWrapper>
		);
	};
}

function agTagEditor(
	classification: UserInstrumentClassificationDto,
): (row: CustomCellEditorProps<UserInstrumentDto>) => React.JSX.Element | string {
	const renderTag = (tagProps: { /* tagId */ value: string }) => {
		return (
			<Row alignItems="center">
				<TagBadge
					label={tagProps.value}
					color={tagColorMap[
						classification.options?.find((o) => o.option === tagProps.value)?.color ?? "GRAY"
					].badgeColor()}
				/>
			</Row>
		);
	};

	return function EditorComponent(
		row: CustomCellEditorProps<UserInstrumentDto, Array<TagValue> | undefined>,
	): React.JSX.Element | string {
		const editableValuesRef = useRef<Array<TagValue>>(row.value ?? []);
		const [editableValues, setEditableValues] = useRefProxyWithState<Array<TagValue>>(
			row.value ?? [],
			editableValuesRef,
		);
		const popupRef = useRef<HTMLDivElement | null>(null);
		const [floatingEl, setFloatingEl] = useState<HTMLDivElement | null>(null);
		const autocompleteRef = useRef<HTMLInputElement | null>(null);
		const autocompleteHandleRef = useRef<AutocompleteHandle | null>(null);
		const autocompleteOptions = useMemo(
			() =>
				classification.options
					?.filter((o) => !editableValues.some((x) => x.option === o.option))
					.map((t) => ({ value: t.option!, label: t.option ?? "-", children: renderTag })) ?? [],
			[editableValues],
		);
		const portalTarget = useMemo(
			() => (row.eGridCell?.closest(".ag-body-viewport") as HTMLElement | null) ?? undefined,
			[row.eGridCell],
		);
		const initialEditableValuesRef = useRef(editableValues);
		useEffect(() => {
			if (initialEditableValuesRef.current.length === 0) {
				autocompleteRef.current?.focus({ preventScroll: true });
			}
		}, []);

		useAgGridRowListener(row, "cellEditingStopped", () => {
			row.node.setDataValue(
				row.column,
				normalizeTags(editableValuesRef.current.filter(({ weight }) => weight != null)),
			);
		});
		useEventListener(floatingEl, "mousedown", (e) => {
			e.stopPropagation();
		});

		return (
			<Column
				innerRef={popupRef}
				gap={16}
				className="bg-white min-w-[224px] rounded-sm border shadow-lg py-2 px-2 overflow-auto max-h-[300px]"
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						e.preventDefault();
						e.stopPropagation();
						row.stopEditing();
					}
				}}
			>
				<Autocomplete
					portalTarget={portalTarget}
					placeholder="Search..."
					value={null}
					innerRef={autocompleteRef}
					options={autocompleteOptions}
					handleRef={autocompleteHandleRef}
					onChange={(option) => {
						if (option != null) {
							setEditableValues([
								...editableValues,
								{
									option,
									weight: undefined,
								},
							]);
						}
						autocompleteHandleRef.current?.clear();
					}}
					className="focus-within:border-[#DFE2E7]"
					size="x-small"
					triggerDataAttrs={{ "data-qualifier": qualifier.component.autocomplete.trigger(row.data.tickerName!) }}
					data-qualifier={qualifier.component.autocomplete.list(row.data.tickerName!)}
					contentRef={setFloatingEl}
				>
					<Text as="div" type="Body/S/Medium" className="py-2 px-2.5">
						{autocompleteOptions.length > 0 ? "Select an option" : "No more tags available"}
					</Text>
				</Autocomplete>
				<TagWeightList
					setValues={setEditableValues}
					showUntagged
					options={classification.options}
					values={editableValues}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							e.stopPropagation();

							// 1 because onKeyDown is dispatched before the last update
							if (autocompleteOptions.length >= 1) {
								autocompleteRef.current?.focus({ preventScroll: true });
							} else {
								e.currentTarget.blur(); // fire input update
								row.stopEditing();
							}
						}
					}}
				/>
			</Column>
		);
	};
}

function normalizeTags(tags: TagValue[]) {
	const totalWeight = Math.max(
		0.01,
		sumArrayLike(tags, ({ weight }) => weight ?? 0),
	);
	if (totalWeight > 100) {
		const normalizedValues = tags.map(({ option, weight }) => ({
			option,
			weight: approxRound(((weight ?? 0) / totalWeight) * 100, 2),
		}));
		return normalizedValues;
	} else {
		return tags;
	}
}
